import { useMemo } from 'react';
import { getDataSource } from '@rsos/components/capstone/Location/helpers';
import { useCallByCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import type {
  EmergencyCallLocation,
  EmergencyCallSecondaryLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import {
  getMostRecentLocations,
  getMostRecentGeoLocation,
} from '@rsos/utils/getMostRecentLoc';

/**
 * Hook to get all of the relevant details of a call.
 */
const useCallDetails = (callerID: string) => {
  const callDetails = useCallByCallerID(callerID);

  const { mostRecentLocation, mostRecentSecondaryLocation } =
    getMostRecentLocations(callDetails);

  // Use the most recent location that has actual coordinates (lat/lng).
  // Messages without location_data create locationless entries that
  // would otherwise shadow a prior valid location (e.g. from DBH), causing an
  // epoch or missing timestamp.
  const mostRecentGeoLocation = useMemo(
    () => getMostRecentGeoLocation(callDetails ?? undefined),
    [callDetails],
  );

  const source = useMemo(() => {
    const primaryLocation =
      mostRecentGeoLocation ??
      mostRecentLocation ??
      mostRecentSecondaryLocation;
    return primaryLocation ? getDataSource(primaryLocation) : null;
  }, [mostRecentGeoLocation, mostRecentLocation, mostRecentSecondaryLocation]);

  const unifiedCallDetails = {
    callerID,
    lastLocation: mostRecentGeoLocation ?? mostRecentSecondaryLocation ?? null,
    lastTimestamp: parseFloat(
      String(
        (mostRecentGeoLocation as EmergencyCallLocation)?.location_time ??
          // When there is no geo location (e.g. a coordinate-free entry),
          // fall back to the call_start_time set on the most recent primary
          // location at ingest so the call's start time is still displayed.
          (mostRecentLocation as EmergencyCallLocation)?.call_start_time ??
          (mostRecentSecondaryLocation as EmergencyCallSecondaryLocation)
            ?.call_start_time ??
          mostRecentSecondaryLocation?.created_time ??
          '',
      ),
    ) as number | null,
    source,
    isAdmin: !!callDetails?.isAdmin,
    callStatus: callDetails?.callStatuses?.at(-1),
  };

  if (Number.isNaN(unifiedCallDetails.lastTimestamp)) {
    unifiedCallDetails.lastTimestamp = null;
  }

  return unifiedCallDetails;
};

export default useCallDetails;
